import type { ShippingProfile, ShippingRule, ShippingSettings } from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EyeOff, FlaskConical, RefreshCw, Save, TrendingDown, TrendingUp, Truck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { ShippingRuleEditor, type Option } from "@/components/ShippingRuleEditor";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type ShippingMethodsState, type SimulationResult } from "@/lib/api";
import { ProfileDialog } from "./AdminShipping";
import { fmtMoney, fmtNumber, fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
const DEFAULT_SETTINGS: ShippingSettings = { customerSees: "own_choice", optimizeByDefault: false, expressKeywords: ["express", "next day", "overnight"] };

/** Versandregeln eines Kunden. Logiful sieht zusätzlich interne Regeln, Sichtbarkeitseinstellung und Simulation. */
export function ShippingRulesPage() {
  const { tenantId, tenant, role, me } = useTenant();
  const staff = me.user.isStaff;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shipping-rules", tenantId], queryFn: () => api.shipping.rules(tenantId) });
  const methodsQ = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), retry: false, staleTime: 5 * 60_000 });
  const packsQ = useQuery({ queryKey: ["packagings"], queryFn: api.shipping.packagings, enabled: staff });
  const [rules, setRules] = useState<ShippingRule[] | null>(null);
  const [settings, setSettings] = useState<ShippingSettings>(DEFAULT_SETTINGS);
  const [sim, setSim] = useState<SimulationResult | null>(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (q.data && rules === null) {
      setRules(q.data.rules);
      if (q.data.settings) setSettings(q.data.settings);
    }
  }, [q.data, rules]);

  const methods: Option[] = useMemo(() => {
    const ws = methodsQ.data?.warehouses ?? [];
    const list = (ws.find((w) => w.active) ?? ws[0])?.shippingMethods ?? [];
    return list.map((m) => ({ value: m.id, label: `${m.name}${m.carrier && !m.name.includes(m.carrier) ? ` (${m.carrier})` : ""}` }));
  }, [methodsQ.data]);
  const packagingOptions: Option[] = (packsQ.data ?? []).map((p) => ({ value: p.id, label: p.name }));
  const dirty = q.data && rules !== null && (JSON.stringify(rules) !== JSON.stringify(q.data.rules) || (staff && JSON.stringify(settings) !== JSON.stringify(q.data.settings ?? DEFAULT_SETTINGS)));

  const save = useMutation({
    mutationFn: () => api.shipping.saveRules(tenantId, rules ?? [], staff ? settings : undefined),
    onSuccess: (r) => {
      toast.success(`Versandregeln gespeichert (Version ${r.version})`, { description: msg("Gilt ab sofort für neue und zurückgehaltene Aufträge.") });
      void qc.invalidateQueries({ queryKey: ["shipping-rules", tenantId] }).then(() => setRules(null));
    },
    onError: (e) => toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Speichern fehlgeschlagen") : "Speichern fehlgeschlagen"),
  });
  const simulate = useMutation({
    mutationFn: () => api.shipping.simulate(tenantId, { rules: rules ?? [], settings, days }),
    onSuccess: setSim,
    onError: () => toast.error("Simulation fehlgeschlagen"),
  });

  if (!canManage(role)) return <div className="px-4 md:px-8 py-8 text-fg-muted">{t("Nur Administratoren können Versandregeln bearbeiten.")}</div>;

  return (
    <div className="pb-24">
      <PageHeader
        title={t("Versandregeln")}
        description={staff ? t("Welche Versandart {name} je Sendung bekommt – inkl. Logiful-interner Optimierung, die der Kunde nicht sieht.", { name: tenant.name }) : t("Welche Versandart je Sendung gewählt wird, z. B. nach Land, Gewicht oder Tag.")}
        actions={<Button variant="primary" icon={<Save className="size-4" />} disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>}
      />
      <div className="px-4 md:px-8 space-y-4 max-w-5xl">
        {staff && (
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3"><EyeOff className="size-4 text-fg-subtle" /><h2 className="font-semibold text-[14px]">{t("Versteckter Modus")}</h2><Badge>{t("nur für Logiful sichtbar")}</Badge></div>
            <div className="grid sm:grid-cols-2 gap-4 text-[13px]">
              <label className="block">
                <span className="text-[12px] font-medium text-fg-muted">{t("Was sieht der Kunde, wenn Logiful die Versandart intern ändert?")}</span>
                <select value={settings.customerSees} onChange={(e) => setSettings({ ...settings, customerSees: e.target.value as ShippingSettings["customerSees"] })} className="mt-1 h-8 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">
                  <option value="own_choice">{t("Nur seine eigene Wahl (Änderung unsichtbar)")}</option>
                  <option value="final_method">{t("Die finale Versandart (ohne Begründung)")}</option>
                </select>
              </label>
              <button type="button" role="switch" aria-checked={settings.optimizeByDefault} onClick={() => setSettings({ ...settings, optimizeByDefault: !settings.optimizeByDefault })} className="flex items-start gap-2.5 text-left">
                <span className={cx("mt-0.5 w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", settings.optimizeByDefault ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", settings.optimizeByDefault && "translate-x-3.5")} /></span>
                <span><span className="block font-medium">{t("Immer günstigste zulässige wählen")}</span><span className="block text-[12px] text-fg-muted">{t("auch ohne passende Regel – intern, nach euren Einkaufspreisen; Express bleibt Express")}</span></span>
              </button>
            </div>
          </Card>
        )}

        {staff && <CustomerMethods methods={methodsQ.data} loading={methodsQ.isLoading} />}

        {q.isLoading || rules === null ? <Skeleton className="h-60 w-full" /> : (
          <ShippingRuleEditor rules={staff ? rules : rules.filter((r) => r.visibility === "customer")} onChange={setRules} methods={methods} packagings={packagingOptions} staff={staff} />
        )}
        {methodsQ.isError && <p className="text-[12.5px] text-fg-muted">{t("Versandarten des Lagers sind gerade nicht abrufbar – vorhandene Regeln bleiben erhalten.")}</p>}

        {staff && (
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <FlaskConical className="size-4 text-fg-subtle" />
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold text-[14px]">{t("Simulation")}</h2>
                <p className="text-[12.5px] text-fg-muted">{t("Rechnet die Regeln oben (auch ungespeichert) gegen die Aufträge der letzten Tage – mit euren Einkaufspreisen.")}</p>
              </div>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
                {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{t("letzte")} {d} {t("Tage")}</option>)}
              </select>
              <Button loading={simulate.isPending} onClick={() => simulate.mutate()}>{t("Simulieren")}</Button>
            </div>
            {sim && <SimulationView sim={sim} methods={methods} />}
          </Card>
        )}
      </div>
    </div>
  );
}

/** Logiful: welche Versandarten hat dieser Kunde (laut FFN) – und gilt dafür ein allgemeines oder ein Kundenprofil? */
function CustomerMethods({ methods, loading }: { methods: ShippingMethodsState | undefined; loading: boolean }) {
  const { tenantId, tenant } = useTenant();
  const qc = useQueryClient();
  const profiles = useQuery({ queryKey: ["shipping-profiles", tenantId], queryFn: () => api.shipping.profiles(tenantId) });
  const [edit, setEdit] = useState<{ methodId: string; name: string; carrier: string | null; def?: ShippingProfile } | null>(null);
  const sync = useMutation({
    mutationFn: () => api.backend.syncShippingMethods(tenantId),
    onSuccess: (r) => {
      qc.setQueryData(["shipping-methods", tenantId], r);
      toast.success("Versandarten von FFN abgerufen", { description: r.added.length || r.removed.length ? [r.added.length && `neu: ${r.added.join(", ")}`, r.removed.length && `entfernt: ${r.removed.join(", ")}`].filter(Boolean).join(" · ") : "Keine Änderungen." });
    },
    onError: () => toast.error("FFN ist gerade nicht erreichbar"),
  });
  const removeOwn = useMutation({
    mutationFn: (methodId: string) => api.shipping.deleteProfile(methodId, tenantId),
    onSuccess: () => { toast.success("Kundenprofil entfernt – es gilt wieder das allgemeine Profil"); void qc.invalidateQueries({ queryKey: ["shipping-profiles", tenantId] }); },
  });
  const active = methods?.warehouses.find((w) => w.active) ?? methods?.warehouses[0];
  const byScope = (methodId: string, scope: "global" | "tenant") => profiles.data?.profiles.find((p) => p.methodId === methodId && (p.scope ?? "global") === scope)?.definition;

  return (
    <Card className="overflow-hidden">
      <div className="px-4 h-11 flex items-center gap-2 border-b border-border">
        <Truck className="size-4 text-fg-subtle" />
        <span className="font-semibold text-[13px]">{t("Versandarten von")} {tenant.name}</span>
        <Badge>{t("nur für Logiful sichtbar")}</Badge>
        <span className="text-[12px] text-fg-subtle">{methods?.syncedAt ? t("laut FFN · abgerufen {v}", { v: fmtRelative(methods.syncedAt) }) : t("laut FFN")}</span>
        <Button size="sm" className="ml-auto" icon={<RefreshCw className={cx("size-3.5", sync.isPending && "animate-spin")} />} disabled={sync.isPending} onClick={() => sync.mutate()}>{t("Von FFN abrufen")}</Button>
      </div>
      {loading ? <div className="p-4"><Skeleton className="h-16 w-full" /></div> : !active ? (
        <p className="px-4 py-4 text-[13px] text-fg-muted">{t("Kein Lager verbunden – Versandarten erscheinen nach dem Verbinden.")}</p>
      ) : (
        <table className="w-full text-left text-[13px]">
          <thead className="text-[11.5px] text-fg-subtle"><tr><th className="h-8 pl-4 pr-3 font-medium">{t("Versandart (")}{active.name})</th><th className="h-8 px-3 font-medium">{t("Profil")}</th><th className="h-8 px-3 font-medium">{t("Einkauf ab")}</th><th className="h-8 pl-3 pr-4" /></tr></thead>
          <tbody>
            {active.shippingMethods.map((m) => {
              const own = byScope(m.id, "tenant");
              const global = byScope(m.id, "global");
              const def = own ?? global;
              const from = def?.prices.length ? Math.min(...def.prices.map((x) => x.priceMinor)) : null;
              return (
                <tr key={m.id} className="border-t border-border">
                  <td className="pl-4 pr-3 py-2"><div className="font-medium">{m.name}</div><div className="text-[12px] text-fg-muted">{m.carrier ?? "—"} · <span className="font-mono">{m.id}</span></div></td>
                  <td className="px-3 py-2">{own ? <Badge tone="info">{own.contract === "customer" ? t("eigener Vertrag") : t("eigene Konditionen")}</Badge> : global ? <Badge>{t("allgemein")}</Badge> : <Badge tone="warning">{t("nicht gepflegt")}</Badge>}</td>
                  <td className="px-3 py-2 tabular-nums">{from != null ? fmtMoney(from, "EUR") : "—"}</td>
                  <td className="pl-3 pr-4 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEdit({ methodId: m.id, name: m.name ?? m.id, carrier: m.carrier, ...(def ? { def } : {}) })}>{own ? t("Bearbeiten") : t("Kundenprofil anlegen")}</Button>
                    {own && <Button size="sm" variant="ghost" onClick={() => { if (confirm("Kundenprofil entfernen? Dann gilt wieder das allgemeine Profil.")) removeOwn.mutate(m.id); }}>{t("Entfernen")}</Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="px-4 py-2.5 border-t border-border text-[12px] text-fg-subtle">{t("Optimiert wird nur zwischen diesen Versandarten. Ein Kundenprofil (z. B. eigene Versandart oder eigene DHL-Konditionen) übersteuert das allgemeine Profil – nur für")} {tenant.name}{t(". Ist es als eigener Versandvertrag markiert, hat es bei Regeln „Name enthält …“ (z. B. Kleinpaket) immer Vorrang.")}</p>
      {edit && <ProfileDialog {...edit} tenantId={tenantId} onClose={() => { setEdit(null); void qc.invalidateQueries({ queryKey: ["shipping-profiles", tenantId] }); }} />}
    </Card>
  );
}

function SimulationView({ sim, methods }: { sim: SimulationResult; methods: Option[] }) {
  const label = (id: string) => methods.find((m) => m.value === id)?.label ?? id;
  const keys = [...new Set([...Object.keys(sim.current.byMethod), ...Object.keys(sim.proposed.byMethod)])];
  const saving = sim.savingsMinor;
  return (
    <div className="mt-4 space-y-3">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-border p-3"><div className="text-[12px] text-fg-muted">{t("Aufträge")}</div><div className="text-[22px] font-semibold tabular-nums">{fmtNumber(sim.orders)}</div><div className="text-[11.5px] text-fg-subtle">{t("letzte")} {sim.days} {t("Tage")}</div></div>
        <div className={cx("rounded-xl border p-3", saving > 0 ? "border-success/30 bg-success-soft/60" : saving < 0 ? "border-danger/25 bg-danger-soft" : "border-border")}>
          <div className="text-[12px] text-fg-muted flex items-center gap-1">{saving >= 0 ? <TrendingDown className="size-3.5" /> : <TrendingUp className="size-3.5" />} {saving >= 0 ? t("Ersparnis") : t("Mehrkosten")}</div>
          <div className="text-[22px] font-semibold tabular-nums">{fmtMoney(Math.abs(saving), "EUR")}</div>
          <div className="text-[11.5px] text-fg-subtle">{fmtMoney(sim.current.costMinor, "EUR")} → {fmtMoney(sim.proposed.costMinor, "EUR")}</div>
        </div>
        <div className="rounded-xl border border-border p-3"><div className="text-[12px] text-fg-muted">{t("Geänderte Versandart")}</div><div className="text-[22px] font-semibold tabular-nums">{fmtNumber(sim.changed)}</div><div className="text-[11.5px] text-fg-subtle">{sim.proposed.holds ? t("{holds} würden zurückgehalten", { holds: sim.proposed.holds }) : t("keine Sperren")}{sim.proposed.unpriced ? t(" · {unpriced} ohne Preis", { unpriced: sim.proposed.unpriced }) : ""}</div></div>
      </div>
      <table className="w-full text-[13px]">
        <thead className="text-[11.5px] text-fg-subtle text-left"><tr><th className="py-1.5 font-medium">{t("Versandart")}</th><th className="py-1.5 font-medium text-right">{t("heute")}</th><th className="py-1.5 font-medium text-right">{t("mit neuen Regeln")}</th></tr></thead>
        <tbody>
          {keys.map((k) => {
            const a = sim.current.byMethod[k] ?? 0;
            const b = sim.proposed.byMethod[k] ?? 0;
            return (
              <tr key={k} className="border-t border-border">
                <td className="py-1.5">{k === "—" ? t("keine passende") : label(k)}</td>
                <td className="py-1.5 text-right tabular-nums">{fmtNumber(a)}</td>
                <td className={cx("py-1.5 text-right tabular-nums", b > a ? "text-success font-medium" : b < a ? "text-fg-muted" : "")}>{fmtNumber(b)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {sim.examples.length > 0 && (
        <details className="text-[12.5px]">
          <summary className="cursor-pointer text-fg-muted">{t("Beispiele (")}{sim.examples.length})</summary>
          <ul className="mt-2 space-y-1">
            {sim.examples.map((e) => (
              <li key={e.orderNumber} className="tabular-nums">{e.orderNumber}: {e.from ? label(e.from) : "—"} → {e.to ? label(e.to) : "—"}{e.savingMinor != null && <span className={cx("ml-1", e.savingMinor >= 0 ? "text-success" : "text-danger")}>({e.savingMinor >= 0 ? "−" : "+"}{fmtMoney(Math.abs(e.savingMinor), "EUR")})</span>}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
